import { OrientedBoundingBox } from "@/components/r3f/utils/oriented-bounding-box";
import { IpxOrder } from "@/modes/ipx-order-mode/ipx-embed-messages";
import { ModeNames } from "@/modes/mode";
import { GUID } from "@faro-lotv/ielement-types";
import { clearStore } from "@faro-lotv/project-source";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Vector3Tuple } from "three";

/** Where the user started the IPX order from */
export type IpxOrderEntryPoint = "sheetsMenu" | "modelsTab";

/** A scan sent to IPX */
export type IpxSentScan = {
  /** Id of the scan */
  id: GUID;

  /** World position of the scanner */
  position: Vector3Tuple;
};

/** A floor detected in the region, the area IPX prices */
export type IpxFloorSummary = {
  /** World height of the floor */
  elevation: number;

  /** Area of the floor in square meters, walls included */
  area: number;
};

/** The floors detected in the region */
export type IpxFloorEstimate = {
  /** Whether the floors are known */
  status: "computing" | "done" | "failed";

  /** The floors, from the lowest */
  floors: IpxFloorSummary[];
};

/** State of the checkout of IPX, where the user orders and pays */
export type IpxCheckoutState = "closed" | "open" | "cancelled";

/** State of the IPX Scan-to-BIM ordering wizard */
export type IpxOrderModeState = {
  /** Where the user started the order from */
  entryPoint?: IpxOrderEntryPoint;

  /** The mode to return to when the wizard closes */
  returnMode: ModeNames;

  /** The clipping box active before the wizard started, restored when it closes */
  returnClippingBox?: OrientedBoundingBox;

  /** The element active before the wizard started, restored when it closes */
  returnActiveElementId?: GUID;

  /** The point cloud of the dataset to model */
  pointCloudId?: GUID;

  /** Number of scans of the dataset, undefined if the dataset can only be ordered as a whole */
  totalScanCount?: number;

  /** The scans the user selected to send only them, empty to send all the scans */
  selectedScanIds: GUID[];

  /** The scans sent to IPX: the selected ones, or all of them if none is selected */
  sentScans: IpxSentScan[];

  /** Size of the whole selected point cloud in bytes */
  pointCloudTotalBytes?: number;

  /** Size of the box around the dense part of the selected point cloud */
  pointCloudSize?: Vector3Tuple;

  /** False to send the whole selection, without clipping it with the region box */
  isClippingBoxEnabled: boolean;

  /** The floors detected in the region, undefined until the detection starts */
  floorEstimate?: IpxFloorEstimate;

  /** Index of the floor the user points at in the list */
  highlightedFloorIndex?: number;

  /** Export size of the region in bytes, from the points sampled in it */
  regionExportBytes?: number;

  /** State of the IPX checkout */
  checkout: IpxCheckoutState;

  /** The order confirmed in the IPX ordering surface */
  order?: IpxOrder;
};

const initialState: IpxOrderModeState = {
  returnMode: "overview",
  selectedScanIds: [],
  sentScans: [],
  isClippingBoxEnabled: true,
  checkout: "closed",
};

const ipxOrderModeSlice = createSlice({
  name: "ipxOrderMode",
  initialState,
  reducers: {
    prepareIpxOrder(
      _,
      action: PayloadAction<
        Pick<
          IpxOrderModeState,
          | "entryPoint"
          | "returnMode"
          | "returnClippingBox"
          | "returnActiveElementId"
        >
      >,
    ) {
      return { ...initialState, ...action.payload };
    },
    setIpxPointCloud(state, action: PayloadAction<GUID>) {
      if (state.pointCloudId === action.payload) return;
      state.pointCloudId = action.payload;
      state.pointCloudSize = undefined;
      state.pointCloudTotalBytes = undefined;
      state.totalScanCount = undefined;
      state.selectedScanIds = [];
      state.sentScans = [];
      state.floorEstimate = undefined;
      state.highlightedFloorIndex = undefined;
      state.regionExportBytes = undefined;
    },
    setIpxScanSelection(
      state,
      action: PayloadAction<
        Pick<
          IpxOrderModeState,
          "totalScanCount" | "selectedScanIds" | "sentScans"
        >
      >,
    ) {
      Object.assign(state, action.payload);
    },
    setIpxPointCloudMetrics(
      state,
      action: PayloadAction<{ size: Vector3Tuple; totalBytes: number }>,
    ) {
      state.pointCloudSize = action.payload.size;
      state.pointCloudTotalBytes = action.payload.totalBytes;
    },
    setIpxClippingBoxEnabled(state, action: PayloadAction<boolean>) {
      state.isClippingBoxEnabled = action.payload;
    },
    setIpxFloorsUpdating(state) {
      // Keeps the last floors while they are detected again
      state.floorEstimate = {
        status: "computing",
        floors: state.floorEstimate?.floors ?? [],
      };
    },
    setIpxFloorEstimate(state, action: PayloadAction<IpxFloorEstimate>) {
      state.floorEstimate = action.payload;
      if ((state.highlightedFloorIndex ?? 0) >= action.payload.floors.length) {
        state.highlightedFloorIndex = undefined;
      }
    },
    setIpxHighlightedFloor(state, action: PayloadAction<number | undefined>) {
      state.highlightedFloorIndex = action.payload;
    },
    setIpxRegionExportBytes(state, action: PayloadAction<number | undefined>) {
      state.regionExportBytes = action.payload;
    },
    setIpxCheckout(state, action: PayloadAction<IpxCheckoutState>) {
      state.checkout = action.payload;
    },
    setIpxOrder(state, action: PayloadAction<IpxOrder | undefined>) {
      state.order = action.payload;
    },
    resetIpxOrder() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(clearStore, () => initialState);
  },
});

export const ipxOrderModeReducer = ipxOrderModeSlice.reducer;

export const {
  prepareIpxOrder,
  setIpxPointCloud,
  setIpxScanSelection,
  setIpxPointCloudMetrics,
  setIpxClippingBoxEnabled,
  setIpxFloorEstimate,
  setIpxFloorsUpdating,
  setIpxHighlightedFloor,
  setIpxRegionExportBytes,
  setIpxCheckout,
  setIpxOrder,
  resetIpxOrder,
} = ipxOrderModeSlice.actions;
